import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Loader2, CheckCircle, Eye, EyeOff, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared shell (re-implemented locally to avoid coupling)
const AuthShell = ({ kicker, title, children }) => (
  <div data-theme="dark" data-atelier="true" className="min-h-screen surface-obsidian flex items-center justify-center p-6 relative">
    <div className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-lime-500 to-transparent z-50" />
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
        backgroundSize: '72px 72px',
        maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 0%, transparent 75%)',
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          'radial-gradient(ellipse 45% 35% at 50% 45%, rgba(255,84,54,0.1) 0%, transparent 70%)',
      }}
    />
    <div className="w-full max-w-md relative z-10">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 mb-5">
          <div
            className="w-9 h-9 bg-lime-500 rounded-md flex items-center justify-center"
            style={{ boxShadow: '0 0 18px rgba(255,84,54,0.45)' }}
          >
            <Sparkles className="w-4 h-4 text-obsidian-900" strokeWidth={2.5} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display font-semibold text-base text-white tracking-tight">Reelscript</span>
            <span className="label-micro text-lime-500">AI</span>
          </div>
        </div>
        {kicker && (
          <div className="label-micro text-lime-500 mb-3 flex items-center justify-center gap-3">
            <span className="h-px w-6 bg-lime-500/60" />
            {kicker}
            <span className="h-px w-6 bg-lime-500/60" />
          </div>
        )}
        <h1 className="font-display font-semibold text-white text-3xl md:text-4xl tracking-tighter leading-tight">
          {title}
        </h1>
      </div>
      <div className="surface-obsidian-elevated rounded-2xl p-7 md:p-8">
        {children}
      </div>
    </div>
  </div>
);

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <AuthShell kicker="INVALID · LINK" title="無效的連結">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-400" strokeWidth={2} />
          </div>
          <p className="text-obsidian-200 text-sm mb-6 leading-relaxed">
            此密碼重設連結無效或已過期，請重新申請密碼重設。
          </p>
          <Button
            onClick={() => navigate('/forgot-password')}
            className="w-full rounded-full h-11 btn-neon font-semibold text-sm"
          >
            重新申請
          </Button>
        </div>
      </AuthShell>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('密碼長度至少 6 個字元'); return; }
    if (password !== confirmPassword) { toast.error('兩次輸入的密碼不一致'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('密碼重設成功');
    } catch (error) {
      toast.error(error.response?.data?.detail || '重設失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell kicker="SUCCESS" title="密碼重設成功">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-lime-500/10 border border-lime-500/30 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-lime-500" strokeWidth={2} />
          </div>
          <p className="text-obsidian-200 text-sm mb-6">您的密碼已成功重設，請使用新密碼登入。</p>
          <Button
            onClick={() => navigate('/')}
            className="w-full rounded-full h-11 btn-neon font-semibold text-sm"
          >
            前往登入
          </Button>
        </div>
      </AuthShell>
    );
  }

  const inputClass = 'rounded-lg h-11 bg-obsidian-800 border-obsidian-600 text-white text-sm focus:border-lime-500/60 placeholder:text-obsidian-400';

  return (
    <AuthShell kicker="RESET · PASSWORD" title="設定新密碼">
      <div className="mb-6 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-lime-500/10 border border-lime-500/30 flex items-center justify-center">
          <Lock className="w-6 h-6 text-lime-500" strokeWidth={2} />
        </div>
        <p className="text-obsidian-200 text-sm">請輸入您的新密碼</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label className="label-micro text-obsidian-300 mb-2 block">NEW · PASSWORD</Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="至少 6 個字元"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputClass} pr-10`}
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-obsidian-300 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label className="label-micro text-obsidian-300 mb-2 block">CONFIRM · PASSWORD</Label>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="再次輸入新密碼"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
            data-testid="reset-confirm-input"
          />
        </div>
        <Button
          type="submit" disabled={loading}
          className="w-full rounded-full h-11 btn-neon font-semibold text-sm"
          data-testid="reset-submit-btn"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />處理中…</> : '重設密碼'}
        </Button>
      </form>
    </AuthShell>
  );
};

export default ResetPassword;
